import uniqid from "uniqid";
import filesize from "filesize";
import chunk from "lodash/chunk";
import { IInitRichTextFileUploadRes } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import {
  IMAGE_COMPRESSION_MAX_SIZE_MB,
  compressImage,
} from "tools/file/compressImage";
import {
  getPendingImage,
  markPendingImageUploaded,
  releasePendingImage,
} from "view/components/RichTextEditor/adapters/pendingImages";
import { dataUriToBlob } from "view/components/RichTextEditor/adapters/dataUriToBlob";
import { IRichTextPendingImage } from "view/components/RichTextEditor/adapters/types";
import { withImageScale } from "./withImageScale";

/**
 * Where the upload token comes from is injected: shows are the first scope,
 * supplier-company (and others) will pass their own factory later without
 * changing this pipeline.
 */
export type RichTextUploadTokenSource = () => Promise<IInitRichTextFileUploadRes>;

/** Show scope — the only one today. New scopes = new sibling factories. */
export const showImageUploadScope = (
  showId: string
): RichTextUploadTokenSource => () => apiV2.richText.initFileUpload({ showId });

export type UploadRichTextImagesResult = {
  html: string;
  tokens: string[];
  // pass to releaseUploadedImages after a successful save
  blobSrcs: string[];
};

// Images upload concurrently rather than one-at-a-time: a submit with several
// pasted images used to serialise an initFileUpload + uploadFile round-trip
// per image. Bounded so a big paste can't open dozens of parallel sockets.
const UPLOAD_CONCURRENCY = 4;

type PendingEntry = {
  src: string;
  pending: IRichTextPendingImage;
  // Every <img> sharing this blob src — a blob pasted twice must still be
  // uploaded once, then have all of its nodes repointed.
  nodes: HTMLImageElement[];
};

const uploadOne = async (
  { src, pending }: PendingEntry,
  initFileUpload: RichTextUploadTokenSource
): Promise<{ url: string; token: string }> => {
  // Already pushed to storage by an earlier attempt (a submit that failed
  // further down) or by another field sharing this blob — reuse it instead of
  // creating a second draft file that nothing will ever reference.
  if (pending.uploaded) {
    return pending.uploaded;
  }

  const { token: initToken, maxSize } = await initFileUpload();

  // Compressed here rather than at insert time so it happens once, right
  // before the bytes leave the browser, and covers every way an image reached
  // the document (toolbar pick, crop, paste, drop). The registry deliberately
  // keeps the ORIGINAL blob — that's what the crop step re-reads.
  // `compressImage` never throws: on failure, or when the result isn't
  // smaller, it hands back the file it was given.
  const file = await compressImage(
    new File([pending.blob], `image.${pending.extension}`, {
      type: pending.blob.type || `image/${pending.extension}`,
    }),
    IMAGE_COMPRESSION_MAX_SIZE_MB.taskDescription
  );

  // Checked after compression and before sending: the server rejects an
  // oversized file with a bare "File too large", which gives no clue which
  // image or what the limit is — and only after the whole payload went over
  // the wire. Measuring the compressed size also means an image that only
  // exceeded the cap before compression is no longer rejected for nothing.
  if (maxSize && file.size > maxSize) {
    throw new Error(
      `An image in the text is too large (${filesize(
        file.size
      )}). The limit is ${filesize(maxSize)} — please resize it and try again.`
    );
  }

  const response = await apiV2.fileUpload.uploadFile({
    file,
    requestId: uniqid(),
    token: initToken,
  });

  if (!response.success || !response.token || !response.url) {
    throw new Error(
      response.details
        ? `Failed to upload an image in the text: ${response.details}`
        : "Failed to upload an image in the text. Please try again."
    );
  }

  const uploaded = { url: response.url, token: response.token };
  markPendingImageUploaded(src, uploaded);

  return uploaded;
};

const DATA_IMAGE_SRC = /^data:image\/[a-z0-9.+-]+[;,]/i;

// SVG data URIs are usually URL-encoded text, not base64 — sometimes raw.
const decodeSvgText = (src: string): Blob | null => {
  const comma = src.indexOf(",");
  if (
    comma === -1 ||
    !/^data:image\/svg\+xml/i.test(src) ||
    /;base64$/i.test(src.slice(0, comma))
  ) {
    return null;
  }
  const payload = src.slice(comma + 1);
  let text = payload;
  try {
    text = decodeURIComponent(payload);
  } catch {
    // Raw markup with a bare `%` (e.g. width="100%").
  }
  return new Blob([text], { type: "image/svg+xml" });
};

const toPendingImage = (src: string): IRichTextPendingImage | undefined => {
  if (src.startsWith("blob:")) {
    return getPendingImage(src);
  }
  if (!DATA_IMAGE_SRC.test(src)) {
    return undefined;
  }
  const decoded = dataUriToBlob(src);
  if (decoded) {
    return { ...decoded, scale: 100 };
  }
  const svg = decodeSvgText(src);
  return svg ? { blob: svg, extension: "svg", scale: 100 } : undefined;
};

export const uploadRichTextImages = async (
  html: string | null | undefined,
  { initFileUpload }: { initFileUpload: RichTextUploadTokenSource }
): Promise<UploadRichTextImagesResult> => {
  if (!html || (!html.includes("blob:") && !html.includes("data:image/"))) {
    return { html: html ?? "", tokens: [], blobSrcs: [] };
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  // Group by src so one blob referenced by several nodes is uploaded once.
  const nodesBySrc = new Map<string, HTMLImageElement[]>();
  Array.from(doc.querySelectorAll<HTMLImageElement>("img")).forEach((img) => {
    const src = img.getAttribute("src") ?? "";
    if (!src.startsWith("blob:") && !src.startsWith("data:image/")) {
      return;
    }
    nodesBySrc.set(src, [...(nodesBySrc.get(src) ?? []), img]);
  });

  if (!nodesBySrc.size) {
    return { html, tokens: [], blobSrcs: [] };
  }

  const entries: PendingEntry[] = [];
  nodesBySrc.forEach((nodes, src) => {
    const pending = toPendingImage(src);
    if (!pending) {
      // A `blob:` src with no registry entry can never be uploaded: it was
      // pasted from another page (its object URL is dead outside that
      // document) or a reload wiped the in-memory registry. Same for a `data:`
      // src that isn't a decodable raster image. Dropping the node is the only
      // correct outcome — throwing would block the save entirely and lose
      // everything else the user just wrote.
      nodes.forEach((node) => node.remove());
      return;
    }
    entries.push({ src, pending, nodes });
  });

  const tokens: string[] = [];
  const blobSrcs: string[] = [];

  for (const batch of chunk(entries, UPLOAD_CONCURRENCY)) {
    // eslint-disable-next-line no-await-in-loop
    const uploads = await Promise.all(
      batch.map((entry) => uploadOne(entry, initFileUpload))
    );

    batch.forEach((entry, index) => {
      const { url, token } = uploads[index];
      // Deduped: two fields sharing one blob must not send the same token
      // twice, since the backend verifies each token it receives.
      if (!tokens.includes(token)) {
        tokens.push(token);
      }
      if (entry.src.startsWith("blob:") && !blobSrcs.includes(entry.src)) {
        blobSrcs.push(entry.src);
      }
      entry.nodes.forEach((node) =>
        node.setAttribute("src", withImageScale(url, entry.pending.scale))
      );
    });
  }

  return { html: doc.body.innerHTML, tokens, blobSrcs };
};

// Call ONLY after the owning save succeeded — on failure, already-uploaded
// files stay `draft: true` server-side and are swept by a daily cron, so
// there is nothing to clean up on the client when a submit aborts. Keeping
// the entries alive on failure is also what lets a retry reuse the uploads
// already done (see `uploaded` on the registry entry).
export const releaseUploadedImages = (blobSrcs: string[]): void => {
  blobSrcs.forEach(releasePendingImage);
};
